import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../hooks/toast';
import {
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  ATable,
  MenuOptions,
  StatusActive,
  DropdownOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import { IProcedure } from '../../shared/interfaces';
import ProcedureCreateEdit from './CreateEdit';
import { currency } from '../../shared/utils/functions';
import { getProcedures } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';
import { useI18n } from '../../shared/i18n';

const Procedures: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const { t } = useI18n();
  const [search, setSearch] = useState<string>('');
  const [procedures, setProcedures] = useState<IProcedure[]>([]);
  const [procedure, setProcedure] = useState<IProcedure | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listProcedures = useCallback(async () => {
    setLoading(true);
    setProcedures([]);
    try {
      const data = await getProcedures(limit, (page - 1) * limit, search);
      setPages(data.count);
      setProcedures(data.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('proceduresPage.toast.errorTitle'),
        description: t('proceduresPage.toast.listError'),
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast, t]);

  const renderModalProcedureCreateEdit = () => {
    if (modalCreate)
      return (
        <ProcedureCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          procedure={procedure}
          setProcedures={setProcedures}
        />
      );
  };

  useEffect(() => {
    listProcedures();
  }, [page, search, limit, listProcedures]);

  return (
    <PageLayout>
      <PageIntro
        badge={t('proceduresPage.intro.badge')}
        icon={<UnorderedListOutlined />}
        title={t('proceduresPage.intro.title')}
        description={t('proceduresPage.intro.description')}
      />
      <MenuOptions
        menu_items={[
          {
            label: t('proceduresPage.actions.refresh'),
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listProcedures(),
          },
          permissionFind([PermissionActions.proceduresCreate])
            ? {
                label: t('proceduresPage.actions.create'),
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setProcedure(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: t('proceduresPage.table.id'),
              width: 100,
              dataIndex: 'code',
            },
            {
              title: t('proceduresPage.table.description'),
              dataIndex: 'description',
            },
            {
              title: t('proceduresPage.table.price'),
              dataIndex: 'price',
              render: (value: number) => <>{currency(value)}</>,
            },
            {
              title: t('proceduresPage.table.status'),
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: t('proceduresPage.table.options'),
              width: 130,
              render: (cell: IProcedure) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.proceduresUpdate])
                      ? {
                          label: t('proceduresPage.actions.edit'),
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setProcedure(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={procedures}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalProcedureCreateEdit()}
    </PageLayout>
  );
};

export default Procedures;
